import Editor from "@monaco-editor/react";
import {connect, useDispatch} from "react-redux";
import {useContext, useEffect, useState, useMemo} from "react";
import {AuthContext} from "../Auth/AuthProvider";
import {getConfig, saveConfig, getEntities, getModels, getTags, smileConcealAPIcall} from "../util/redux/asyncActions";
import {
    Button, Alert, Switch, Card, Select, Input, InputNumber, Space, Typography,
    Slider, Tooltip, Table, Modal, Popconfirm, Form, message, AutoComplete
} from "antd";
import {changeConfig, resetSuccess} from "../util/redux/index";
import {PlusOutlined, DeleteOutlined, QuestionCircleOutlined, SaveOutlined, CodeOutlined, FormOutlined, ArrowUpOutlined, ArrowDownOutlined} from "@ant-design/icons";
import {isLegacyConfig, migrateConfig} from "../util/configMigration";

const {Text} = Typography;

// Recognizer sources in default precedence order - must match the
// service-side RECOGNIZER_SOURCES in anonymization_handler.py
const RECOGNIZER_SOURCES = [
    {key: "dynamicTagging", label: "Dynamic Tagging"},
    {key: "privacyFilter", label: "Privacy Filter"},
    {key: "patterns", label: "Pattern Recognizers (Regex)"},
    {key: "trainedTagging", label: "Trained Tagging"},
    {key: "llmTagging", label: "LLM Tagging (Gateway)"}
];

const select = state => {
    const config = state.request.config;
    const error = state.request.error;
    const unsavedConfig = state.request.unsavedConfig;
    const success = state.request.success;
    const tags = state.request.tags;
    const availableModels = state.request.models;
    const dynamicTagging = state.request.dynamicTagging;
    const entities = state.request.entities;
    const allEntities = state.request.all_entities;

    return {
        config, error, unsavedConfig, success, tags, availableModels,
        dynamicTagging, entities, allEntities
    };
};

const ConfigEditor = (props) => {

    const dispatch = useDispatch();
    const userManager = useContext(AuthContext);
    const [isCodeView, setIsCodeView] = useState(false);
    const [isEntityModalOpen, setIsEntityModalOpen] = useState(false);
    const [newEntityType, setNewEntityType] = useState("");
    const [newEntityLabels, setNewEntityLabels] = useState([]);
    const [migrationAlert, setMigrationAlert] = useState(false);
    const [recognizers, setRecognizers] = useState(null);
    const [savingRecs, setSavingRecs] = useState(false);
    const [llmKeyStatus, setLlmKeyStatus] = useState(null);
    const [llmModels, setLlmModels] = useState([]);

    const {config, unsavedConfig, error, success, tags, availableModels, allEntities} = props;

    const allModelOptions = useMemo(() => {
        if (!availableModels) return [];
        return availableModels.map(m => ({
            value: m.type === "custom" ? `./models/${m.model}` : m.model,
            label: m.type === "custom" ? `${m.model} (custom)` : m.model,
            lang: m.lang
        }));
    }, [availableModels]);

    const availableLanguages = useMemo(() => {
        if (!availableModels) return [];
        const langs = [...new Set(availableModels.map(m => m.lang))];
        return langs.map(lang => ({value: lang, label: lang}));
    }, [availableModels]);

    const parsedConfig = useMemo(() => {
        try {
            return config ? JSON.parse(config) : null;
        } catch (e) {
            return null;
        }
    }, [config]);

    const entityTableData = useMemo(() => {
        if (!parsedConfig?.entities) return [];
        return Object.entries(parsedConfig.entities).map(([type, cfg]) => ({
            key: type,
            type,
            labels: cfg.labels || []
        }));
    }, [parsedConfig]);

    const availableEntityOptions = useMemo(() => {
        if (!allEntities || allEntities.length === 0) return [];
        const existingTypes = entityTableData.map(e => e.type);
        return allEntities
            .filter(e => !existingTypes.includes(e))
            .map(e => ({value: e, label: e}));
    }, [allEntities, entityTableData]);

    useEffect(() => {
        if (config === undefined) {
            dispatch(getConfig({userManager}));
        }
    }, [config]);

    useEffect(() => {
        if (config && isLegacyConfig(config)) {
            const migrated = migrateConfig(config);
            dispatch(changeConfig(JSON.stringify(migrated, null, 2)));
            setMigrationAlert(true);
        }
    }, [config]);

    useEffect(() => {
        if (tags.length === 0) {
            dispatch(getEntities({userManager}));
        }
    }, [tags]);

    useEffect(() => {
        if (!allEntities || allEntities.length === 0) {
            dispatch(getTags({userManager}));
        }
    }, [allEntities]);

    useEffect(() => {
        if (!availableModels || availableModels.length === 0) {
            dispatch(getModels({userManager}));
        }
    }, [availableModels]);

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                dispatch(resetSuccess());
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    useEffect(() => {
        smileConcealAPIcall({
            url: `${window._env_.REACT_APP_API_URL}/recognizers`,
            requestOptions: {method: "GET", headers: {"content-type": "application/json"}},
            userManager
        }).then((r) => setRecognizers((r && r.recognizers) || []))
          .catch(() => setRecognizers([]));
        smileConcealAPIcall({
            url: `${window._env_.REACT_APP_API_URL}/llmKeyStatus`,
            requestOptions: {method: "GET", headers: {"content-type": "application/json"}},
            userManager
        }).then((r) => setLlmKeyStatus(r || null))
          .catch(() => setLlmKeyStatus(null));
        smileConcealAPIcall({
            url: `${window._env_.REACT_APP_API_URL}/llmModels`,
            requestOptions: {method: "GET", headers: {"content-type": "application/json"}},
            userManager
        }).then((r) => setLlmModels((r && r.models) || []))
          .catch(() => setLlmModels([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateRecognizer = (i, updates) => {
        setRecognizers(recognizers.map((r, idx) => (idx === i ? {...r, ...updates} : r)));
    };
    const updateRecognizerPattern = (i, updates) => {
        setRecognizers(recognizers.map((r, idx) => {
            if (idx !== i) return r;
            const patterns = [...(r.patterns || [{}])];
            patterns[0] = {name: patterns[0]?.name || "pattern", score: 0.6, ...patterns[0], ...updates};
            return {...r, patterns};
        }));
    };
    const saveRecognizers = () => {
        setSavingRecs(true);
        smileConcealAPIcall({
            url: `${window._env_.REACT_APP_API_URL}/recognizers`,
            requestOptions: {
                method: "POST",
                headers: {"content-type": "application/json"},
                body: JSON.stringify({recognizers})
            },
            userManager
        }).then((r) => {
            if (r && r.recognizers) {
                setRecognizers(r.recognizers);
                message.success(r.warning ? `Saved with warning: ${r.warning}` : "Pattern recognizers saved and activated");
            } else {
                message.error("Save failed" + ((r && r.error) ? ": " + r.error : " (check regex syntax)"));
            }
        }).catch((e) => message.error("Save failed (check regex syntax): " + e))
          .finally(() => setSavingRecs(false));
    };

    const onChange = (conf) => {
        dispatch(changeConfig(conf));
    };

    const updateConfigFromUI = (models, entities, dynamicTagging, privacyFilter, trainedTagging) => {
        // callers that don't pass privacyFilter/trainedTagging keep the current blocks
        const pf = privacyFilter !== undefined ? privacyFilter : (parsedConfig?.privacyFilter || null);
        const tt = trainedTagging !== undefined ? trainedTagging : (parsedConfig?.trainedTagging || null);
        const newConfig = {
            configuration: {
                nlp_engine_name: "spacy",
                models: models
            }
        };
        if (dynamicTagging && dynamicTagging.model) {
            newConfig.dynamicTagging = dynamicTagging;
        }
        if (pf && pf.model) {
            newConfig.privacyFilter = pf;
        }
        if (tt && (tt.entities || tt.enabled === false)) {
            newConfig.trainedTagging = tt;
        }
        if (parsedConfig?.patterns && Object.keys(parsedConfig.patterns).length > 0) {
            newConfig.patterns = parsedConfig.patterns;
        }
        if (parsedConfig?.llmTagging && Object.keys(parsedConfig.llmTagging).length > 0) {
            newConfig.llmTagging = parsedConfig.llmTagging;
        }
        if (parsedConfig?.ignoreValues && parsedConfig.ignoreValues.length > 0) {
            newConfig.ignoreValues = parsedConfig.ignoreValues;
        }
        if (parsedConfig?.recognizerOrder && parsedConfig.recognizerOrder.length > 0) {
            newConfig.recognizerOrder = parsedConfig.recognizerOrder;
        }
        if (entities && Object.keys(entities).length > 0) {
            newConfig.entities = entities;
        }
        dispatch(changeConfig(JSON.stringify(newConfig, null, 2)));
    };

    // normalized order: configured entries first (valid ones only), missing sources appended
    const recognizerOrder = useMemo(() => {
        const configured = (parsedConfig?.recognizerOrder || []).filter(
            s => RECOGNIZER_SOURCES.some(r => r.key === s)
        );
        return [...configured, ...RECOGNIZER_SOURCES.map(r => r.key).filter(k => !configured.includes(k))];
    }, [parsedConfig]);

    const handleRecognizerOrderMove = (idx, dir) => {
        const next = [...recognizerOrder];
        const j = idx + dir;
        if (j < 0 || j >= next.length) return;
        [next[idx], next[j]] = [next[j], next[idx]];
        dispatch(changeConfig(JSON.stringify({...parsedConfig, recognizerOrder: next}, null, 2)));
    };

    const handleLlmField = (field, value) => {
        const block = {...(parsedConfig?.llmTagging || {})};
        if (value === "" || value === undefined || value === null) {
            delete block[field];
        } else {
            block[field] = value;
        }
        dispatch(changeConfig(JSON.stringify({...parsedConfig, llmTagging: block}, null, 2)));
    };

    // enabled unless the block explicitly says enabled: false
    const isRecognizerEnabled = (key) =>
        !(parsedConfig?.[key] && parsedConfig[key].enabled === false);

    const handleRecognizerToggle = (key, enabled) => {
        const block = {...(parsedConfig?.[key] || {})};
        if (enabled) {
            delete block.enabled;
        } else {
            block.enabled = false;
        }
        dispatch(changeConfig(JSON.stringify({...parsedConfig, [key]: block}, null, 2)));
    };

    const handleAddModel = () => {
        if (!parsedConfig) return;
        const currentModels = parsedConfig.configuration?.models || [];
        const usedLangs = currentModels.map(m => m.lang_code);
        const unusedLang = availableLanguages.find(l => !usedLangs.includes(l.value));
        if (!unusedLang) return;
        const firstModelForLang = allModelOptions.find(m => m.lang === unusedLang.value);
        const newModel = firstModelForLang
            ? {lang_code: firstModelForLang.lang, model_name: firstModelForLang.value}
            : {lang_code: unusedLang.value, model_name: ""};
        const newModels = [...currentModels, newModel];
        updateConfigFromUI(newModels, parsedConfig.entities || {}, parsedConfig.dynamicTagging || null);
    };

    const handleRemoveModel = (index) => {
        if (!parsedConfig) return;
        const currentModels = parsedConfig.configuration?.models || [];
        const newModels = currentModels.filter((_, i) => i !== index);
        updateConfigFromUI(newModels, parsedConfig.entities || {}, parsedConfig.dynamicTagging || null);
    };

    const handleModelChange = (index, field, value) => {
        if (!parsedConfig) return;
        const currentModels = parsedConfig.configuration?.models || [];
        const newModels = currentModels.map((model, i) => {
            if (i === index) {
                if (field === "lang_code") {
                    const firstModelForLang = allModelOptions.find(m => m.lang === value);
                    return {
                        ...model,
                        lang_code: value,
                        model_name: firstModelForLang ? firstModelForLang.value : ""
                    };
                }
                return {...model, [field]: value};
            }
            return model;
        });
        updateConfigFromUI(newModels, parsedConfig.entities || {}, parsedConfig.dynamicTagging || null);
    };

    const handleDynamicTaggingChange = (field, value) => {
        if (!parsedConfig) return;
        const currentDT = parsedConfig.dynamicTagging || {model: "", threshold: 0.6};
        const newDT = {...currentDT, [field]: value};
        updateConfigFromUI(
            parsedConfig.configuration?.models || [],
            parsedConfig.entities || {},
            newDT.model ? newDT : null
        );
    };

    const PRIVACY_FILTER_LABELS = [
        "private_person", "private_address", "private_email", "private_phone",
        "private_url", "private_date", "account_number", "secret"
    ];

    const handlePrivacyFilterChange = (field, value) => {
        if (!parsedConfig) return;
        const current = parsedConfig.privacyFilter || {model: "", threshold: 0.5, entities: {}};
        const newPF = {...current, [field]: value};
        updateConfigFromUI(
            parsedConfig.configuration?.models || [],
            parsedConfig.entities || {},
            parsedConfig.dynamicTagging || null,
            newPF.model ? newPF : null
        );
    };

    const handlePFMappingChange = (entityType, labels) => {
        if (!parsedConfig) return;
        const current = parsedConfig.privacyFilter || {model: "", threshold: 0.5, entities: {}};
        const newPFEntities = {...(current.entities || {})};
        if (!labels || labels.length === 0) {
            delete newPFEntities[entityType];
        } else {
            newPFEntities[entityType] = {labels: labels};
        }
        updateConfigFromUI(
            parsedConfig.configuration?.models || [],
            parsedConfig.entities || {},
            parsedConfig.dynamicTagging || null,
            {...current, entities: newPFEntities}
        );
    };

    const handleTrainedTaggingEntities = (entityList) => {
        if (!parsedConfig) return;
        updateConfigFromUI(
            parsedConfig.configuration?.models || [],
            parsedConfig.entities || {},
            parsedConfig.dynamicTagging || null,
            undefined,
            (entityList && entityList.length > 0) ? {entities: entityList} : null
        );
    };

    const handleIgnoreValuesChange = (values) => {
        if (!parsedConfig) return;
        const newConfig = JSON.parse(JSON.stringify(parsedConfig));
        if (values && values.length > 0) {
            newConfig.ignoreValues = values;
        } else {
            delete newConfig.ignoreValues;
        }
        dispatch(changeConfig(JSON.stringify(newConfig, null, 2)));
    };

    const handleEntityLabelsChange = (entityType, newLabels) => {
        if (!parsedConfig) return;
        const newEntities = {...(parsedConfig.entities || {}), [entityType]: {labels: newLabels}};
        updateConfigFromUI(
            parsedConfig.configuration?.models || [],
            newEntities,
            parsedConfig.dynamicTagging || null
        );
    };

    const handleRemoveEntity = (entityType) => {
        if (!parsedConfig) return;
        const newEntities = {...(parsedConfig.entities || {})};
        delete newEntities[entityType];
        updateConfigFromUI(
            parsedConfig.configuration?.models || [],
            newEntities,
            parsedConfig.dynamicTagging || null
        );
    };

    const handleAddEntity = () => {
        if (!newEntityType || !parsedConfig) return;
        const newEntities = {
            ...(parsedConfig.entities || {}),
            [newEntityType]: {labels: newEntityLabels || []}
        };
        updateConfigFromUI(
            parsedConfig.configuration?.models || [],
            newEntities,
            parsedConfig.dynamicTagging || null
        );
        setNewEntityType("");
        setNewEntityLabels([]);
        setIsEntityModalOpen(false);
    };

    const entityColumns = [
        {
            title: "Entity Type",
            dataIndex: "type",
            key: "type",
            width: 160,
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: () => (
                <Space>
                    Dynamic Tagging Labels
                    <Tooltip title="Labels that the model uses to detect this entity type. More labels = broader detection.">
                        <QuestionCircleOutlined/>
                    </Tooltip>
                </Space>
            ),
            dataIndex: "labels",
            key: "labels",
            render: (labels, record) => (
                <Select
                    mode="tags"
                    style={{width: "100%"}}
                    placeholder="Add labels..."
                    value={labels}
                    onChange={(newLabels) => handleEntityLabelsChange(record.type, newLabels)}
                    aria-label={`Labels for ${record.type}`}
                />
            )
        },
        {
            title: "",
            key: "actions",
            width: 50,
            render: (_, record) => (
                <Popconfirm title={`Remove ${record.type}?`} onConfirm={() => handleRemoveEntity(record.type)}>
                    <Button type="text" danger icon={<DeleteOutlined/>} size="small" aria-label={`Remove ${record.type}`}/>
                </Popconfirm>
            )
        }
    ];

    const renderUIView = () => {
        if (!parsedConfig) {
            return <Alert message="Invalid JSON format" type="error" style={{margin: 24}}/>;
        }

        const models = parsedConfig.configuration?.models || [];

        return (
            <div style={{maxWidth: 960, margin: "0 auto", padding: "24px 32px"}}>

                {/* Recognizer Priority Section */}
                <Card
                    title={
                        <Space>
                            Recognizer Priority
                            <Tooltip title="Switch recognizers on/off and set their precedence. When two recognizers claim overlapping text with DIFFERENT entity types, the one higher in this list wins. Overlaps of the same type are always merged (privacy first). Within one recognizer the higher score wins.">
                                <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                            </Tooltip>
                        </Space>
                    }
                    style={{marginBottom: 20}}
                >
                    {recognizerOrder.map((key, idx) => {
                        const src = RECOGNIZER_SOURCES.find(r => r.key === key);
                        const enabled = isRecognizerEnabled(key);
                        return (
                            <div key={key} style={{
                                display: "flex", alignItems: "center", gap: 8,
                                padding: "6px 12px", marginBottom: 6,
                                background: enabled ? "#fafafa" : "#f5f5f5",
                                border: "1px solid #f0f0f0", borderRadius: 6,
                                opacity: enabled ? 1 : 0.6
                            }}>
                                <Text type="secondary" style={{width: 18}}>{idx + 1}.</Text>
                                <Text style={{flex: 1}} type={enabled ? undefined : "secondary"}>
                                    {src?.label || key}{!enabled && " (disabled)"}
                                </Text>
                                <Switch
                                    size="small"
                                    checked={enabled}
                                    onChange={(v) => handleRecognizerToggle(key, v)}
                                    aria-label={`Enable ${src?.label || key}`}
                                />
                                <Button size="small" type="text" icon={<ArrowUpOutlined/>}
                                        disabled={idx === 0}
                                        onClick={() => handleRecognizerOrderMove(idx, -1)}
                                        aria-label={`Move ${src?.label || key} up`}/>
                                <Button size="small" type="text" icon={<ArrowDownOutlined/>}
                                        disabled={idx === recognizerOrder.length - 1}
                                        onClick={() => handleRecognizerOrderMove(idx, 1)}
                                        aria-label={`Move ${src?.label || key} down`}/>
                            </div>
                        );
                    })}
                    <Text type="secondary" style={{fontSize: 12, display: "block", marginTop: 8}}>
                        Example: with Pattern Recognizers ranked above Trained Tagging, a license plate
                        matched by regex keeps its LICENSE_PLATE label even if a language model claims
                        the same text as MONEY.
                    </Text>
                </Card>

                {/* Dynamic Tagging Section */}
                <Card
                    title={
                        <Space>
                            Dynamic Tagging
                            <Tooltip title="Label-based entity detection. Set a model name to activate. The system will detect entities based on the labels you define below.">
                                <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                            </Tooltip>
                        </Space>
                    }
                    style={{marginBottom: 20}}
                >
                    <div style={{display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24}}>
                        <Form.Item label="Model Name" style={{marginBottom: 0}}>
                            <Input
                                placeholder="e.g. urchade/gliner_multi_pii-v1"
                                value={parsedConfig.dynamicTagging?.model || ""}
                                onChange={(e) => handleDynamicTaggingChange("model", e.target.value)}
                                aria-label="Dynamic Tagging model name"
                            />
                        </Form.Item>
                        <Form.Item
                            label={
                                <Space>
                                    Threshold
                                    <Tooltip title="Detection confidence (0.0 - 1.0). Lower = more entities found. Higher = more precise.">
                                        <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                                    </Tooltip>
                                </Space>
                            }
                            style={{marginBottom: 0}}
                        >
                            <Slider
                                min={0}
                                max={1}
                                step={0.05}
                                value={parsedConfig.dynamicTagging?.threshold ?? 0.6}
                                onChange={(val) => handleDynamicTaggingChange("threshold", val)}
                                marks={{0: "0", 0.5: "0.5", 1: "1.0"}}
                                disabled={!parsedConfig.dynamicTagging?.model}
                                aria-label="Detection confidence threshold"
                            />
                        </Form.Item>
                    </div>
                    {!parsedConfig.dynamicTagging?.model && (
                        <Text type="secondary" style={{fontSize: 12, display: "block", marginTop: 8}}>
                            Enter a model name to enable Dynamic Tagging.
                        </Text>
                    )}
                </Card>

                {/* Privacy Filter Section */}
                <Card
                    title={
                        <Space>
                            Privacy Filter
                            <Tooltip title="Additional local PII model (token classification, 8 categories). Set a model name to activate. Categories are mapped to your entity types below.">
                                <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                            </Tooltip>
                        </Space>
                    }
                    style={{marginBottom: 20}}
                >
                    <div style={{display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24}}>
                        <Form.Item label="Model Name" style={{marginBottom: 0}}>
                            <Input
                                placeholder="e.g. openai/privacy-filter"
                                value={parsedConfig.privacyFilter?.model || ""}
                                onChange={(e) => handlePrivacyFilterChange("model", e.target.value)}
                                aria-label="Privacy filter model name"
                            />
                        </Form.Item>
                        <Form.Item
                            label={
                                <Space>
                                    Threshold
                                    <Tooltip title="Detection confidence (0.0 - 1.0). Lower = more entities found. Higher = more precise.">
                                        <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                                    </Tooltip>
                                </Space>
                            }
                            style={{marginBottom: 0}}
                        >
                            <Slider
                                min={0}
                                max={1}
                                step={0.05}
                                value={parsedConfig.privacyFilter?.threshold ?? 0.5}
                                onChange={(val) => handlePrivacyFilterChange("threshold", val)}
                                marks={{0: "0", 0.5: "0.5", 1: "1.0"}}
                                disabled={!parsedConfig.privacyFilter?.model}
                                aria-label="Privacy filter threshold"
                            />
                        </Form.Item>
                    </div>
                    <Alert
                        type="info"
                        showIcon
                        style={{marginTop: 12}}
                        message="Fixed category set"
                        description="Unlike Dynamic Tagging, the Privacy Filter only detects 8 fixed, model-defined categories: private_person, private_address, private_email, private_phone, private_url, private_date, account_number, secret. Custom labels are not supported — only the mapping of these categories to your entity types below."
                    />
                    {!parsedConfig.privacyFilter?.model ? (
                        <Text type="secondary" style={{fontSize: 12, display: "block", marginTop: 8}}>
                            Enter a model name to enable the Privacy Filter.
                        </Text>
                    ) : (
                        <div style={{marginTop: 16}}>
                            <Text strong style={{fontSize: 13, display: "block", marginBottom: 8}}>
                                Category Mapping
                                <Tooltip title="Which filter categories feed which entity type. Categories not mapped anywhere are ignored (e.g. private_date, if you don't want every date masked).">
                                    <QuestionCircleOutlined style={{color: "#bfbfbf", marginLeft: 6}}/>
                                </Tooltip>
                            </Text>
                            {[...new Set([
                                ...Object.keys(parsedConfig.privacyFilter?.entities || {}),
                                ...Object.keys(parsedConfig.entities || {})
                            ])].sort().map((entityType) => (
                                <div key={entityType} style={{display: "grid", gridTemplateColumns: "160px 1fr", gap: 12, marginBottom: 6, alignItems: "center"}}>
                                    <Text strong>{entityType}</Text>
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        style={{width: "100%"}}
                                        placeholder="not mapped"
                                        value={parsedConfig.privacyFilter?.entities?.[entityType]?.labels || []}
                                        options={PRIVACY_FILTER_LABELS.map(l => ({value: l}))}
                                        onChange={(labels) => handlePFMappingChange(entityType, labels)}
                                        aria-label={`Privacy filter categories for ${entityType}`}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </Card>

                {/* LLM Tagging Section */}
                <Card
                    title={
                        <Space>
                            LLM Tagging (Gateway)
                            <Tooltip title="Optional recognizer using a large language model behind an OpenAI-compatible gateway (e.g. LiteLLM). Set URL and model to activate. Your entity types and Dynamic Tagging labels are passed to the model automatically.">
                                <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                            </Tooltip>
                        </Space>
                    }
                    style={{marginBottom: 20}}
                >
                    <div style={{display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 16}}>
                        <Form.Item label="Gateway URL (OpenAI-compatible)" style={{marginBottom: 12}}>
                            <Input
                                value={parsedConfig.llmTagging?.url || ""}
                                onChange={(e) => handleLlmField("url", e.target.value)}
                                placeholder="https://llm-gateway.example.com/v1 (empty = disabled)"
                                aria-label="LLM gateway base URL"
                            />
                        </Form.Item>
                        <Form.Item
                            label={
                                <Space>
                                    Model
                                    {llmModels.length > 0 && (
                                        <Tooltip title="Models the gateway offers for the configured key (loaded from the saved gateway URL). Free text is still allowed.">
                                            <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                                        </Tooltip>
                                    )}
                                </Space>
                            }
                            style={{marginBottom: 12}}
                        >
                            <AutoComplete
                                value={parsedConfig.llmTagging?.model || ""}
                                onChange={(v) => handleLlmField("model", v)}
                                options={llmModels.map((m) => ({value: m}))}
                                filterOption={(input, o) => o.value.toLowerCase().includes(input.toLowerCase())}
                                style={{width: "100%"}}
                                placeholder="e.g. gpt-5.4"
                                aria-label="LLM model name"
                            />
                        </Form.Item>
                        <Form.Item
                            label={
                                <Space>
                                    API
                                    <Tooltip title="Which OpenAI API the gateway is called with. Auto: try chat/completions first and remember a broken gateway. Responses: skip the probe (e.g. LiteLLM chatgpt-provider, whose chat translation fails slowly). Chat: for servers without the newer /responses API (e.g. local models).">
                                        <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                                    </Tooltip>
                                </Space>
                            }
                            style={{marginBottom: 12}}
                        >
                            <Select
                                value={parsedConfig.llmTagging?.transport || "auto"}
                                onChange={(v) => handleLlmField("transport", v === "auto" ? "" : v)}
                                style={{width: "100%"}}
                                options={[
                                    {value: "auto", label: "Auto"},
                                    {value: "responses", label: "Responses"},
                                    {value: "chat", label: "Chat Completions"}
                                ]}
                                aria-label="Gateway API transport"
                            />
                        </Form.Item>
                        <Form.Item
                            label={
                                <Space>
                                    Score
                                    <Tooltip title="LLMs do not produce calibrated per-span confidences, so every LLM finding gets this fixed score. It decides ties against other recognizers' results within the same priority rank.">
                                        <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                                    </Tooltip>
                                </Space>
                            }
                            style={{marginBottom: 12}}
                        >
                            <InputNumber
                                min={0}
                                max={1}
                                step={0.05}
                                value={parsedConfig.llmTagging?.score ?? 0.85}
                                onChange={(v) => handleLlmField("score", v)}
                                style={{width: "100%"}}
                                aria-label="Fixed score assigned to LLM findings"
                            />
                        </Form.Item>
                    </div>
                    <Form.Item
                        label={
                            <Space>
                                API key environment variable
                                <Tooltip title="The key itself is NEVER stored in this config - it stays on the service (local .env or a Kubernetes secret). This field only names the environment variable to read it from.">
                                    <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                                </Tooltip>
                            </Space>
                        }
                        style={{marginBottom: 12}}
                    >
                        <Space>
                            <Input
                                value={parsedConfig.llmTagging?.apiKeyEnv || ""}
                                onChange={(e) => handleLlmField("apiKeyEnv", e.target.value)}
                                placeholder="LLM_GATEWAY_KEY"
                                style={{width: 260}}
                                aria-label="Environment variable holding the API key"
                            />
                            {llmKeyStatus && (
                                <Text type={llmKeyStatus.set ? "success" : "danger"} style={{fontSize: 12}}>
                                    {llmKeyStatus.set
                                        ? `● key available on service (${llmKeyStatus.env})`
                                        : `○ ${llmKeyStatus.env} not set on service`}
                                </Text>
                            )}
                        </Space>
                    </Form.Item>
                    <Form.Item label="System prompt (optional)" style={{marginBottom: 0}}>
                        <Input.TextArea
                            value={parsedConfig.llmTagging?.systemPrompt || ""}
                            onChange={(e) => handleLlmField("systemPrompt", e.target.value)}
                            placeholder="Empty = built-in default. Your entity types, their labels and the required output format are always appended - a custom prompt only replaces the instruction part."
                            autoSize={{minRows: 2, maxRows: 6}}
                            aria-label="Custom system prompt for LLM tagging"
                        />
                    </Form.Item>
                    <Text type="secondary" style={{fontSize: 12, display: "block", marginTop: 8}}>
                        One gateway call per text - slower and potentially external, so ideal as a
                        showcase or precision layer, not as the only recognizer. A gateway failure
                        never breaks anonymization: the local recognizers keep running.
                    </Text>
                </Card>

                {/* Trained Tagging Restriction Section */}
                <Card
                    title={
                        <Space>
                            Trained Tagging
                            <Tooltip title="Language models (below) detect entities with pretrained news NER plus your custom-trained models. On ITSM texts the generic NER often produces false positives - restrict it here to the entity types it should contribute.">
                                <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                            </Tooltip>
                        </Space>
                    }
                    style={{marginBottom: 20}}
                >
                    <Form.Item
                        label="Allowed entity types"
                        style={{marginBottom: 0}}
                    >
                        <Select
                            mode="multiple"
                            allowClear
                            style={{width: "100%"}}
                            placeholder="All entity types (no restriction)"
                            value={parsedConfig.trainedTagging?.entities || []}
                            options={Object.keys(parsedConfig.entities || {}).map(t => ({value: t}))}
                            onChange={handleTrainedTaggingEntities}
                            aria-label="Entity types Trained Tagging may contribute"
                        />
                    </Form.Item>
                    <Text type="secondary" style={{fontSize: 12, display: "block", marginTop: 8}}>
                        Empty = Trained Tagging may contribute all entity types. Recommended: restrict to your
                        custom-trained entities (e.g. MONEY) and let Dynamic Tagging / Privacy Filter handle the rest.
                    </Text>

                    <div style={{marginTop: 20}}>
                        <Space style={{marginBottom: 8}}>
                            <Text strong>Language Models</Text>
                            <Tooltip title="Pre-trained spaCy models for entity detection. Select one model per language.">
                                <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                            </Tooltip>
                        </Space>
                        {models.map((model, index) => {
                            const usedLangs = models
                                .filter((_, i) => i !== index)
                                .map(m => m.lang_code);
                            const availableLangsForModel = availableLanguages.filter(
                                lang => !usedLangs.includes(lang.value)
                            );

                            return (
                                <div key={index} style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 2fr auto",
                                    alignItems: "end",
                                    gap: 16,
                                    marginBottom: 12,
                                    padding: "12px 16px",
                                    background: "#fafafa",
                                    borderRadius: 8,
                                    border: "1px solid #f5f5f5",
                                }}>
                                    <Form.Item label="Language" style={{marginBottom: 0}}>
                                        <Select
                                            value={model.lang_code}
                                            onChange={(value) => handleModelChange(index, "lang_code", value)}
                                            style={{width: "100%"}}
                                            options={availableLangsForModel}
                                            aria-label={`Language for model ${index + 1}`}
                                        />
                                    </Form.Item>
                                    <Form.Item label="Model" style={{marginBottom: 0}}>
                                        <Select
                                            value={model.model_name}
                                            onChange={(value) => handleModelChange(index, "model_name", value)}
                                            style={{width: "100%"}}
                                            options={allModelOptions.filter(m => m.lang === model.lang_code)}
                                            aria-label={`Model for language ${model.lang_code}`}
                                        />
                                    </Form.Item>
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined/>}
                                        onClick={() => handleRemoveModel(index)}
                                        aria-label={`Remove ${model.lang_code} model`}
                                        style={{marginBottom: 1}}
                                    />
                                </div>
                            );
                        })}
                        {models.length < availableLanguages.length && (
                            <Button
                                type="dashed"
                                onClick={handleAddModel}
                                icon={<PlusOutlined/>}
                                style={{width: "100%", marginTop: models.length > 0 ? 4 : 0}}
                            >
                                Add Language Model
                            </Button>
                        )}
                    </div>
                </Card>

                {/* Ignore List Section */}
                <Card
                    title={
                        <Space>
                            Ignore List
                            <Tooltip title="Values that are never reported as findings, no matter which recognizer detects them - e.g. generic role nouns like 'der Kunde'. Case-insensitive, exact value match.">
                                <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                            </Tooltip>
                        </Space>
                    }
                    style={{marginBottom: 20}}
                >
                    <Select
                        mode="tags"
                        style={{width: "100%"}}
                        placeholder="Add values to ignore, e.g. der Kunde"
                        value={parsedConfig.ignoreValues || []}
                        onChange={handleIgnoreValuesChange}
                        tokenSeparators={[","]}
                        aria-label="Values never reported as findings"
                    />
                    <Text type="secondary" style={{fontSize: 12, display: "block", marginTop: 8}}>
                        Generic role words ("der Kunde", "Mitarbeiter") are often semantically close to
                        PERSON and get flagged by the ML models. Listing them here suppresses those
                        false positives across all recognizers.
                    </Text>
                </Card>

                {/* Pattern Recognizers Section */}
                <Card
                    title={
                        <Space>
                            Pattern Recognizers (Regex)
                            <Tooltip title="Rule-based recognizers for fully regular formats (IPs, ticket numbers, internal IDs). They complement the ML recognizers with 100% deterministic matches and apply immediately after saving.">
                                <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                            </Tooltip>
                        </Space>
                    }
                    extra={
                        <Button type="primary" size="small" loading={savingRecs} onClick={saveRecognizers}>
                            Save Recognizers
                        </Button>
                    }
                    style={{marginBottom: 20}}
                >
                    {(recognizers || []).map((rec, i) => (
                        <div key={i} style={{border: "1px solid #f0f0f0", borderRadius: 6, padding: 12, marginBottom: 10}}>
                            <div style={{display: "grid", gridTemplateColumns: "1fr 1fr 100px 110px 40px", gap: 8, marginBottom: 8}}>
                                <Input
                                    placeholder="Name, e.g. Ticketnummer"
                                    value={rec.name}
                                    onChange={(e) => updateRecognizer(i, {name: e.target.value})}
                                />
                                <Input
                                    placeholder="Entity, e.g. TICKET_ID"
                                    value={rec.supported_entity}
                                    onChange={(e) => updateRecognizer(i, {supported_entity: e.target.value.toUpperCase()})}
                                />
                                <Select
                                    value={rec.supported_language || "de"}
                                    onChange={(v) => updateRecognizer(i, {supported_language: v})}
                                    options={["de", "en", "es"].map((l) => ({value: l}))}
                                />
                                <InputNumber
                                    min={0}
                                    max={1}
                                    step={0.05}
                                    value={rec.patterns?.[0]?.score ?? 0.6}
                                    onChange={(v) => updateRecognizerPattern(i, {score: v})}
                                    addonBefore="⚖"
                                />
                                <Popconfirm title={`Remove ${rec.name || "recognizer"}?`}
                                            onConfirm={() => setRecognizers(recognizers.filter((_, idx) => idx !== i))}>
                                    <Button type="text" danger icon={<DeleteOutlined/>}/>
                                </Popconfirm>
                            </div>
                            <Input
                                addonBefore="Regex"
                                style={{fontFamily: "monospace"}}
                                placeholder="e.g. \\bINC\\d{9,12}\\b"
                                value={rec.patterns?.[0]?.regex || ""}
                                onChange={(e) => updateRecognizerPattern(i, {regex: e.target.value})}
                            />
                            <Select
                                mode="tags"
                                style={{width: "100%", marginTop: 8}}
                                placeholder="Context words (optional) — raise confidence when nearby, e.g. Konto, IBAN"
                                value={rec.context || []}
                                onChange={(v) => updateRecognizer(i, {context: v})}
                            />
                        </div>
                    ))}
                    <Button
                        icon={<PlusOutlined/>}
                        type="dashed"
                        onClick={() => setRecognizers([...(recognizers || []), {
                            name: "",
                            supported_entity: "",
                            supported_language: "de",
                            patterns: [{name: "pattern", regex: "", score: 0.6}],
                            context: []
                        }])}
                    >
                        add pattern recognizer
                    </Button>
                </Card>

                {/* Entities Section */}
                <Card
                    title={
                        <Space>
                            Entities
                            <Tooltip title="Define entity types for detection. Add labels for Dynamic Tagging — entities without labels will only be detected by Trained Tagging.">
                                <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                            </Tooltip>
                        </Space>
                    }
                >
                    <Table
                        dataSource={entityTableData}
                        columns={entityColumns}
                        pagination={false}
                        size="small"
                        locale={{emptyText: "No entities configured. Add entities below."}}
                    />
                    <Button
                        type="dashed"
                        icon={<PlusOutlined/>}
                        onClick={() => setIsEntityModalOpen(true)}
                        style={{width: "100%", marginTop: 12}}
                    >
                        Add Entity
                    </Button>
                </Card>
            </div>
        );
    };

    return (
        <>
            {/* Toolbar */}
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 24px",
                backgroundColor: "#fff",
                borderBottom: "1px solid #f0f0f0",
            }}>
                <Button
                    onClick={() => dispatch(saveConfig({userManager, config}))}
                    disabled={!unsavedConfig}
                    type="primary"
                    icon={<SaveOutlined/>}
                    aria-label="Save configuration"
                >
                    Save
                </Button>
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 12px",
                    background: "#fafafa",
                    borderRadius: 6,
                    border: "1px solid #f0f0f0",
                }}>
                    <FormOutlined style={{fontSize: 13, color: !isCodeView ? "#ef7721" : "#8c8c8c"}}/>
                    <Switch
                        checked={isCodeView}
                        onChange={setIsCodeView}
                        size="small"
                        aria-label="Toggle between UI and code editor"
                    />
                    <CodeOutlined style={{fontSize: 13, color: isCodeView ? "#ef7721" : "#8c8c8c"}}/>
                </div>
                {isCodeView && (
                    <Button
                        size="small"
                        onClick={() => {
                            try {
                                JSON.parse(config);
                                message.success("Valid JSON");
                            } catch (e) {
                                message.error(`Invalid JSON: ${e.message}`);
                            }
                        }}
                    >
                        Validate JSON
                    </Button>
                )}
                {unsavedConfig && (
                    <Text type="warning" style={{fontSize: 12, color: "#faad14"}}>Unsaved changes</Text>
                )}
                {success && (
                    <Alert
                        message="Configuration saved!"
                        type="success"
                        showIcon
                        banner
                        style={{marginLeft: "auto", padding: "4px 12px"}}
                    />
                )}
            </div>

            {error && (
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    style={{margin: "16px 24px 0"}}
                />
            )}
            {migrationAlert && (
                <Alert
                    message="Configuration migrated"
                    description="Your configuration has been converted from the old format (removeTags) to the new format (entities). Please review and save."
                    type="info"
                    showIcon
                    closable
                    onClose={() => setMigrationAlert(false)}
                    style={{margin: "16px 24px 0"}}
                />
            )}

            {isCodeView ? (
                <Editor
                    defaultValue={config}
                    value={config}
                    defaultLanguage={"json"}
                    onChange={onChange}
                    height={"calc(100vh - 160px)"}
                />
            ) : (
                renderUIView()
            )}

            {/* Add Entity Modal */}
            <Modal
                title="Add Entity"
                open={isEntityModalOpen}
                onOk={handleAddEntity}
                onCancel={() => {
                    setIsEntityModalOpen(false);
                    setNewEntityType("");
                    setNewEntityLabels([]);
                }}
                okButtonProps={{disabled: !newEntityType}}
            >
                <Form layout="vertical" style={{marginTop: 16}}>
                    <Form.Item label="Entity Type" required>
                        <Select
                            showSearch
                            placeholder="Select entity type..."
                            value={newEntityType || undefined}
                            onChange={(val) => setNewEntityType(val)}
                            options={availableEntityOptions}
                            aria-label="New entity type"
                        />
                    </Form.Item>
                    <Form.Item
                        label={
                            <Space>
                                Dynamic Tagging Labels
                                <Tooltip title="Optional. Labels that help detect this entity type. Example: for PERSON add 'person', 'name', 'first_name', 'last_name'.">
                                    <QuestionCircleOutlined/>
                                </Tooltip>
                            </Space>
                        }
                    >
                        <Select
                            mode="tags"
                            placeholder="Type labels and press Enter..."
                            value={newEntityLabels}
                            onChange={setNewEntityLabels}
                            aria-label="Labels for new entity"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};
export default connect(select)(ConfigEditor);
